import type { ConnectorState, RecommendedConnector } from '../connector-promo-config-types.ts';

/**
 * Maps short connector names used in TEAM_CONNECTOR_MAP to the connector type
 * keys used by the third-party-connector-configuration package's configsMap.
 */
const CONNECTOR_KEY_MAP: Record<string, string> = {
	github: 'github-connector',
	drive: 'platform-google-drive-connector',
	sharepoint: 'platform-sharepoint-connector',
	slack: 'slack-connector',
	figma: 'figma-connector',
};

/**
 * Team type to recommended connector keys mapping.
 * Each team type maps to an ordered list of 3 short connector names.
 * Use CONNECTOR_KEY_MAP to resolve the full connector type key.
 */
const TEAM_CONNECTOR_MAP: Record<string, string[]> = {
	SOFTWARE_DEVELOPMENT: ['github', 'drive', 'sharepoint'],
	PROJECT_MANAGEMENT: ['drive', 'sharepoint', 'github'],
	DATA_SCIENCE: ['drive', 'sharepoint', 'github'],
	MARKETING: ['drive', 'sharepoint', 'figma'],
	DESIGN: ['figma', 'drive', 'sharepoint'],
	CUSTOMER_SERVICE: ['sharepoint', 'drive', 'slack'],
	HUMAN_RESOURCES: ['sharepoint', 'drive', 'slack'],
	FINANCE: ['sharepoint', 'drive', 'slack'],
	LEGAL: ['sharepoint', 'drive', 'slack'],
	IT: ['sharepoint', 'github', 'drive'],
	SALES: ['drive', 'sharepoint', 'slack'],
	DEFAULT: ['drive', 'sharepoint', 'github'],
};

/**
 * Maps a team type and the current connector states to an ordered list of
 * recommended connectors that the user has NOT yet set up.
 *
 * Connectors with an enabled data source (hasEnabledDataSource === true) are
 * filtered out. They should not appear in the sidebar, empty state, or Nth
 * promo surfaces. The only place a set-up connector's name is referenced is
 * the post-connect promo, returned as connectedAppName.
 *
 * Connector state is derived from the admin_getAllDataSources AGG query.
 * A connector is "set up" when it has at least one AdminDataSource with
 * enabled === true.
 *
 * @param teamType - The uppercased team type (e.g., 'SOFTWARE_DEVELOPMENT')
 * @param connectorStates - A record mapping connector type keys to their state
 * @returns Unconnected recommendations and the first connected app's display name
 */
export function getRecommendedConnectors(
	teamType: string | undefined,
	connectorStates: Record<string, ConnectorState>,
): { recommendedConnectors: RecommendedConnector[]; connectedAppName: string | undefined } {
	const shortKeys =
		teamType && TEAM_CONNECTOR_MAP[teamType]
			? TEAM_CONNECTOR_MAP[teamType]
			: TEAM_CONNECTOR_MAP.DEFAULT;

	const recommendedConnectors: RecommendedConnector[] = [];
	let connectedAppName: string | undefined;

	for (const shortKey of shortKeys) {
		// Resolve to the full connector type key used by configsMap
		const connectorTypeKey = CONNECTOR_KEY_MAP[shortKey] ?? shortKey;
		const state = connectorStates[connectorTypeKey];
		const isSetUp = state?.hasEnabledDataSource === true;

		if (isSetUp) {
			if (connectedAppName === undefined) {
				connectedAppName = state?.displayName ?? shortKey;
			}
		} else {
			recommendedConnectors.push({
				key: connectorTypeKey,
				displayName: state?.displayName ?? shortKey,
			});
		}
	}

	return { recommendedConnectors, connectedAppName };
}
